// @LTG-ID: contact-confirmation
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: source `Contact.html` lines 1098-1192 (v4),
//        ADR-024 Amendment A (confirmation triple-phase timings)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A2)
// @OWNER: web
//
// CHG-0083-M-A2 — Confirmation screen.
//
// Shown after 201 success from POST /api/contact. Renders:
//   1. Icon triple-phase motion (per ADR-024 Amendment A): drawCircle 0.7s →
//      drawCheck2 0.4s @ +0.5s → sparkleIn 0.4s @ +0.9s.
//   2. Category-aware "Thanks, <firstName>." headline + supporting copy
//      (sub-copy + response-day expectation + closing kicker), all 6 COPY
//      blocks ported verbatim from v4 source lines 1105-1142.
//   3. Receipt card with reference, routed-to, expected-reply, confirmation-to,
//      from-name+business+state.
//   4. "Back to home" + "Read while you wait" CTAs (links to /, /journals).
//
// A.3 #7: v4 confirmation triple-phase motion applies to the ICON only.
// ORCH-0087 point 5 also wants headline/copy fade-up phases. Honoring BOTH:
// keep v4 icon motion as contracted by ADR-024 Amendment A; ADD fade-up
// keyframes for headline + supporting block + reference card (defined in
// contact-shared.css as ltgcConfirmHeadlineIn, ltgcConfirmSubIn, ltgcConfirmCardIn).
//
// A.3 #8: v4 confirmation has NO inline newsletter form. ORCH-0087 point 5
// says "Skip if v4 design doesn't include it; honor v4." → SKIPPED.

import { CATEGORIES } from "./StepCategory";
import { US_STATES } from "./validators";

// Motion timings per ADR-024 Amendment A (sourced from v4 README §"All
// animations" line 140). Defined inline at consumer site per Amendment A
// §"Implementation contract" item 1.
const CONTACT_CONFIRM_CIRCLE_DURATION_MS = 700;
const CONTACT_CONFIRM_CHECK_DURATION_MS = 400;
const CONTACT_CONFIRM_CHECK_DELAY_MS = 500;
const CONTACT_CONFIRM_SPARKLE_DURATION_MS = 400;
const CONTACT_CONFIRM_SPARKLE_DELAY_MS = 900;

// COPY map ported verbatim from v4 source `Contact.html` lines 1105-1142.
// 6 categories — each carries:
//   headlinePrefix/Em/Suffix  — for "Welcome to the <em>X</em>, {first}." pattern
//   sub                       — supporting paragraph (with bold response-time)
//   responseDays              — receipt-card "Expected reply" value
//   kicker                    — final italic gold line
const COPY = {
    snapshot: {
        headlinePrefix: "Welcome to the ",
        headlineEm: "conversation",
        headlineSuffix: ", ",
        sub: (
            <>
                The Strategic Snapshot desk has your note. We&apos;re the team
                that builds the report behind the methodology — and we read
                everything that comes in. Expect a thoughtful reply within{" "}
                <strong>one business day</strong>, from a real human, signed.
            </>
        ),
        responseDays: "1 business day",
        kicker: "Let's talk growth.",
    },
    membership: {
        headlinePrefix: "Glad you&apos;re ",
        headlineEm: "thinking ahead",
        headlineSuffix: ", ",
        sub: (
            <>
                The Growth Membership team will be in touch within{" "}
                <strong>one business day</strong>. If you&apos;re already a
                Snapshot owner, we&apos;ll line up your access. If not,
                we&apos;ll walk you through how the front door works first.
            </>
        ),
        responseDays: "1 business day",
        kicker: "The plan compounds.",
    },
    support: {
        headlinePrefix: "We&apos;ve got it, ",
        headlineEm: null, // emphasis on first name itself
        headlineSuffix: ".",
        sub: (
            <>
                You&apos;re a KGOB client and that means we move first. Your
                message is in front of the client services desk now. Reply
                within <strong>one business day</strong>, faster if it&apos;s
                blocking.
            </>
        ),
        responseDays: "1 business day",
        kicker: "We're on it.",
    },
    press: {
        headlinePrefix: "On the wire, ",
        headlineEm: null,
        headlineSuffix: ".",
        sub: (
            <>
                Our communications lead has your note. We respond to working
                journalists{" "}
                <strong>same business day where possible</strong>. If your
                deadline is tight, mention it in a quick reply and we&apos;ll
                prioritize.
            </>
        ),
        responseDays: "Same business day where possible",
        kicker: "Quotable, on the record.",
    },
    partnership: {
        headlinePrefix: "Partners welcome, ",
        headlineEm: null,
        headlineSuffix: ".",
        sub: (
            <>
                The right person at KGOB will be in touch within{" "}
                <strong>two business days</strong>. Most of our best client
                work comes through advisors and peer firms; we treat partner
                notes the same way.
            </>
        ),
        responseDays: "2 business days",
        kicker: "Let's build something.",
    },
    other: {
        headlinePrefix: "Message logged, ",
        headlineEm: null,
        headlineSuffix: ".",
        sub: (
            <>
                It&apos;s in our hands. We&apos;ll route it to the right person
                at KGOB and get back within <strong>two business days</strong>.
                If we need anything else from you first, we&apos;ll ask.
            </>
        ),
        responseDays: "2 business days",
        kicker: "Thanks for writing in.",
    },
};

export const Confirmation = ({ data, referenceKey, onRestart }) => {
    const cat = CATEGORIES.find((c) => c.key === data.category);
    const first = (data.firstName || "").trim();
    const fullName = [data.firstName, data.middleName, data.lastName]
        .filter((s) => s && s.trim())
        .join(" ")
        .trim();
    const stateEntry = US_STATES.find((s) => s[0] === data.state);
    const copy = COPY[data.category] || COPY.other;

    // Build headline JSX — supports the two v4 patterns:
    //   pattern A: "Welcome to the <em>conversation</em>, Sarah."
    //   pattern B: "We've got it, <em>Sarah</em>." (first name itself emphasized)
    const headlineJsx = copy.headlineEm ? (
        <>
            {copy.headlinePrefix}
            <em>{copy.headlineEm}</em>
            {copy.headlineSuffix}
            {first}.
        </>
    ) : (
        <>
            {copy.headlinePrefix}
            <em>{first}</em>.
        </>
    );

    return (
        <section
            className="ltgc-step ltgc-step-confirmation"
            data-testid="contact-confirmation"
            aria-labelledby="ltgc-confirm-heading"
        >
            <div className="ltgc-confirm-mark" aria-hidden="true">
                <svg viewBox="0 0 120 120">
                    <circle
                        className="ltgc-confirm-circle"
                        cx="60"
                        cy="60"
                        r="49"
                        style={{
                            animationDuration: `${CONTACT_CONFIRM_CIRCLE_DURATION_MS}ms`,
                        }}
                    />
                    <path
                        className="ltgc-confirm-check"
                        d="M40 62 L54 76 L82 46"
                        style={{
                            animationDuration: `${CONTACT_CONFIRM_CHECK_DURATION_MS}ms`,
                            animationDelay: `${CONTACT_CONFIRM_CHECK_DELAY_MS}ms`,
                        }}
                    />
                    <g
                        className="ltgc-confirm-sparkle"
                        style={{
                            animationDuration: `${CONTACT_CONFIRM_SPARKLE_DURATION_MS}ms`,
                            animationDelay: `${CONTACT_CONFIRM_SPARKLE_DELAY_MS}ms`,
                        }}
                    >
                        <circle cx="22" cy="28" r="3" fill="#B08D3E" />
                        <circle cx="98" cy="34" r="2.5" fill="#B08D3E" />
                        <circle cx="100" cy="90" r="3" fill="#B08D3E" />
                        <circle cx="18" cy="92" r="2.5" fill="#B08D3E" />
                    </g>
                </svg>
            </div>
            <p
                className="ltgc-confirm-greeting"
                data-testid="contact-confirm-greeting"
            >
                Submission received · {cat ? cat.label : "Other"}
            </p>
            <h2
                id="ltgc-confirm-heading"
                className="ltgc-confirm-h"
                data-testid="contact-confirm-heading"
            >
                {headlineJsx}
            </h2>
            <p
                className="ltgc-confirm-sub"
                data-testid="contact-confirm-sub"
            >
                {copy.sub}
            </p>
            <div
                className="ltgc-confirm-card"
                data-testid="contact-confirm-card"
            >
                <div className="ltgc-confirm-row ltgc-confirm-row-ref">
                    <span className="ltgc-confirm-k">Your reference</span>
                    <span
                        className="ltgc-confirm-v"
                        data-testid="contact-confirm-reference"
                    >
                        {referenceKey}
                    </span>
                </div>
                <div className="ltgc-confirm-row">
                    <span className="ltgc-confirm-k">Routed to</span>
                    <span className="ltgc-confirm-v">
                        {cat ? cat.label : "Other"} desk
                    </span>
                </div>
                <div className="ltgc-confirm-row">
                    <span className="ltgc-confirm-k">Expected reply</span>
                    <span className="ltgc-confirm-v">{copy.responseDays}</span>
                </div>
                <div className="ltgc-confirm-row">
                    <span className="ltgc-confirm-k">Confirmation to</span>
                    <span
                        className="ltgc-confirm-v"
                        data-testid="contact-confirm-email"
                    >
                        {data.email}
                    </span>
                </div>
                <div className="ltgc-confirm-row">
                    <span className="ltgc-confirm-k">From</span>
                    <span className="ltgc-confirm-v">
                        {fullName} · {data.businessName} ·{" "}
                        {stateEntry ? stateEntry[1] : data.state}
                    </span>
                </div>
            </div>
            <p
                className="ltgc-confirm-kicker"
                data-testid="contact-confirm-kicker"
            >
                {copy.kicker}
            </p>
            <div className="ltgc-confirm-cta">
                <a
                    href="/"
                    className="ltgc-btn ltgc-btn-secondary"
                    data-testid="contact-confirm-home"
                >
                    Back to home
                </a>
                <a
                    href="/journals"
                    className="ltgc-btn ltgc-btn-primary"
                    data-testid="contact-confirm-journals"
                >
                    Read while you wait{" "}
                    <span className="ltgc-arrow">→</span>
                </a>
            </div>
            {onRestart && (
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-ghost"
                    style={{ margin: "0 auto", display: "flex" }}
                    onClick={onRestart}
                    data-testid="contact-confirm-restart"
                >
                    Send another message
                </button>
            )}
            <p className="ltgc-confirm-note">
                Bookmark this page or save the reference above. It identifies
                your submission in our system.
            </p>
        </section>
    );
};

export default Confirmation;
